// Shrink a picked image to something worth embedding.
//
// The encoder sees 384px; anything larger is bytes on the wire for no accuracy. Doing it on
// a canvas also means the original file never leaves the browser — only the downscaled JPEG
// is sent, it is embedded in memory, and nothing is stored.
//
// Extracted from `labsSearch/UploadSearch.tsx`, which was the first caller and now imports
// it from here: the Browse button needs the identical transform, and two copies of a
// "downscale to exactly what the model wants" rule is how they drift apart.

/** The longest side the encoder is fed. */
const MAX_EDGE = 384;

export interface DownscaledImage {
  /** Base64 JPEG body (no `data:` prefix) — what the embedder takes. */
  b64: string;
  /** Full `data:` URL — what a thumbnail renders. */
  preview: string;
}

export async function downscaleForEmbedding(file: File): Promise<DownscaledImage> {
  const url = URL.createObjectURL(file);
  try {
    const img = new Image();
    img.src = url;
    await img.decode();
    const scale = Math.min(1, MAX_EDGE / Math.max(img.width, img.height));
    const w = Math.max(1, Math.round(img.width * scale));
    const h = Math.max(1, Math.round(img.height * scale));
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Could not read the image");
    ctx.drawImage(img, 0, 0, w, h);
    const dataUrl = canvas.toDataURL("image/jpeg", 0.85);
    return { b64: dataUrl.split(",")[1] ?? "", preview: dataUrl };
  } finally {
    // In a `finally` so a decode failure does not leak the object URL, which the original
    // copy of this code did on every rejected file.
    URL.revokeObjectURL(url);
  }
}

/**
 * Pack a query vector for the filter payload: base64 of the raw `Float32Array` bytes.
 *
 * `search.vector` hands back `number[]`; the filter is a `Record<string, string>` that rides
 * in every `search.load` body, so 1152 floats go as 6.1 kB here against ~15 kB as JSON.
 * `vecFromB64` in apps/pictufy/src/lib/vectorSearch.ts is the other end.
 */
export function vecToB64(vec: number[]): string {
  const bytes = new Uint8Array(Float32Array.from(vec).buffer);
  let binary = "";
  // Chunked: `String.fromCharCode(...bytes)` on 4,608 bytes is fine today, but the spread
  // is an argument list and blows the stack at larger dimensions.
  for (let i = 0; i < bytes.length; i += 8192) {
    binary += String.fromCharCode(...bytes.subarray(i, i + 8192));
  }
  return btoa(binary);
}
